import type { Row, SheetData } from 'write-excel-file/browser';
import type { ChartPublicationCaption } from './chart-export.util';
import { formatIndicatorNumber } from './number-format';
import { formatTableCell, type TableColumn, type TableModel, type TableRow } from './table-model';

/**
 * Ficha acima da grade no CSV e no XLSX.
 *
 * `true` — a ficha entra antes dos dados e a grade ganha a coluna País.
 * `false` — só a grade, como antes.
 */
export const USE_PUBLICATION_TABLE_EXPORT = true;

export type TableExportFormat = 'csv' | 'xlsx';

export interface TablePrefaceField {
  label: string;
  value: string;
}

/** Separador `;` e BOM UTF-8 — é o que o Excel em pt-BR abre sem pedir assistente de importação. */
const CSV_SEPARATOR = ';';
/** Uma casa decimal no Excel; o aplicativo mostra a vírgula conforme o idioma do usuário. */
const EXCEL_ONE_DECIMAL = '#,##0.0';

export function slugifyFileName(value: string): string {
  return (
    value
      .toLowerCase()
      .normalize('NFD')
      // Remove os acentos separados pelo NFD para "ação" virar "acao", não "a-a-o".
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'dados'
  );
}

/**
 * Valor da célula para export. Medidas numéricas saem com uma casa decimal
 * (CSV em pt-BR, XLSX com formato de número). Texto como `N/A` permanece texto.
 */
function exportCellValue(column: TableColumn, row: TableRow): string | number | null {
  const raw = row[column.key];
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const numeric = Number(raw.replace(',', '.'));
    if (column.numeric && raw.trim() !== '' && Number.isFinite(numeric)) return numeric;
    return raw;
  }
  return formatTableCell(column, row);
}

function csvCell(column: TableColumn, row: TableRow): string | number | null {
  const value = exportCellValue(column, row);
  if (typeof value === 'number' && column.numeric) return formatIndicatorNumber(value);
  return value;
}

function xlsxCell(column: TableColumn, row: TableRow): Row[number] {
  const value = exportCellValue(column, row);
  if (typeof value === 'number' && column.numeric) return { value, format: EXCEL_ONE_DECIMAL };
  return value;
}

function escapeCsv(value: string | number | null): string {
  if (value == null) return '';
  const text = String(value);
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function tableToCsv(model: TableModel, preface: readonly TablePrefaceField[] = []): string {
  const header = model.columns.map((column) => escapeCsv(column.label)).join(CSV_SEPARATOR);
  const body = model.rows.map((row) =>
    model.columns.map((column) => escapeCsv(csvCell(column, row))).join(CSV_SEPARATOR),
  );
  const lines = preface.map((field) => `${escapeCsv(field.label)}${CSV_SEPARATOR}${escapeCsv(field.value)}`);
  if (lines.length) lines.push('');
  return [...lines, header, ...body].join('\r\n');
}

/**
 * Linhas da ficha. Campos vazios ficam de fora. A linha de códigos só entra
 * quando a grade exportada contém `N/A` ou `NA`.
 */
export function buildTablePreface(
  model: TableModel,
  caption: ChartPublicationCaption | null | undefined,
): TablePrefaceField[] {
  if (!caption) return [];
  const labels = caption.fieldLabels ?? {};
  const fields: TablePrefaceField[] = [];
  const push = (label: string, value?: string) => {
    const text = value?.trim();
    if (!text) return;
    fields.push({ label, value: text });
  };

  push(labels.title ?? 'Título', caption.title);
  push(labels.unit ?? 'Unidade', caption.unit);
  push(labels.territory ?? 'Abrangência territorial', caption.territory);
  push(labels.period ?? 'Período', caption.period);
  push(labels.disaggregations ?? 'Desagregações', caption.disaggregations);
  push(caption.sourceLabel ?? 'Fonte', caption.source);
  push(labels.notes ?? 'Notas', caption.notes);
  push(caption.updatedAtLabel ?? 'Última atualização', caption.updatedAt);

  const codes = missingCodes(model);
  if (codes.length) {
    const phrase = labels.missingValue ?? '{code} = informação não disponível';
    push(
      labels.codes ?? 'Códigos',
      codes.map((code) => phrase.replaceAll('{code}', code)).join('; '),
    );
  }

  return fields;
}

const COUNTRY_COLUMN_KEY = '__pais';

/**
 * Primeira coluna da grade exportada, com o país em todas as linhas.
 * Só entra quando a ficha tem território. UF, região e município continuam
 * nas colunas que já existem. Uma coluna de país já presente não é repetida.
 */
export function withCountryColumn(
  model: TableModel,
  caption: ChartPublicationCaption | null | undefined,
): TableModel {
  const country = countryName(caption?.territory);
  if (!country) return model;
  if (model.columns.some((column) => isCountryColumn(column.label) || isCountryColumn(column.key))) {
    return model;
  }
  const label = caption?.fieldLabels?.country ?? 'País';
  return {
    columns: [{ key: COUNTRY_COLUMN_KEY, label }, ...model.columns],
    rows: model.rows.map((row) => ({ [COUNTRY_COLUMN_KEY]: country, ...row })),
  };
}

function countryName(territory: string | undefined): string {
  const text = territory?.trim();
  if (!text) return '';
  const separator = ' — ';
  const index = text.indexOf(separator);
  return (index === -1 ? text : text.slice(0, index)).trim();
}

function isCountryColumn(name: string): boolean {
  const key = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  return key === 'pais' || key === 'country' || key === COUNTRY_COLUMN_KEY;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadTableCsv(
  model: TableModel,
  fileName: string,
  preface: readonly TablePrefaceField[] = [],
): boolean {
  if (typeof document === 'undefined' || !model.columns.length) return false;
  const blob = new Blob(['﻿' + tableToCsv(model, preface)], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, withExtension(fileName, 'csv'));
  return true;
}

/**
 * XLSX real (zip + XML). A lib entra por `import()` dinâmico para não pesar no bundle
 * inicial — só é baixada quando o usuário aciona o export.
 *
 * Com `preface`, a ficha entra antes da grade e congela ficha + cabeçalho juntos.
 */
export async function downloadTableXlsx(
  model: TableModel,
  fileName: string,
  sheetName = 'Dados',
  preface: readonly TablePrefaceField[] = [],
): Promise<boolean> {
  if (typeof document === 'undefined' || !model.columns.length) return false;

  const { default: writeXlsxFile } = await import('write-excel-file/browser');

  const header: Row = model.columns.map((column) => ({
    value: column.label,
    fontWeight: 'bold' as const,
    alignVertical: 'center' as const,
    backgroundColor: HEADER_BACKGROUND,
  }));

  const body: Row[] = model.rows.map((row) => model.columns.map((column) => xlsxCell(column, row)));

  const valueWidth = prefaceValueWidth(preface);
  const prefaceRows: Row[] = preface.map((field) => [
    { value: field.label, fontWeight: 'bold' as const, alignVertical: 'top' as const },
    {
      value: field.value,
      wrap: true,
      alignVertical: 'top' as const,
      height: prefaceRowHeight(field.value, valueWidth),
    },
  ]);

  const sheetData: SheetData = prefaceRows.length ? [...prefaceRows, [], header, ...body] : [header, ...body];

  await writeXlsxFile(sheetData, {
    sheet: sheetName.slice(0, 31),
    stickyRowsCount: prefaceRows.length ? prefaceRows.length + 2 : 1,
    columns: sheetColumnWidths(model, preface, valueWidth),
  }).toFile(withExtension(fileName, 'xlsx'));

  return true;
}

const HEADER_BACKGROUND = '#F3F6F7';

function missingCodes(model: TableModel): string[] {
  let hasNaSlash = false;
  let hasNa = false;
  for (const row of model.rows) {
    for (const column of model.columns) {
      const raw = row[column.key];
      if (typeof raw !== 'string') continue;
      const text = raw.trim();
      if (/^n\/a$/i.test(text)) hasNaSlash = true;
      else if (/^na$/i.test(text)) hasNa = true;
    }
  }
  const codes: string[] = [];
  if (hasNaSlash) codes.push('N/A');
  if (hasNa) codes.push('NA');
  return codes;
}

function prefaceValueWidth(preface: readonly TablePrefaceField[]): number {
  const longest = preface.reduce((max, field) => Math.max(max, field.value.split('\n')[0]?.length ?? 0), 0);
  return Math.min(Math.max(longest + 2, 24), 72);
}

function prefaceRowHeight(value: string, widthChars: number): number {
  const lines = value.split('\n').reduce((count, paragraph) => {
    return count + Math.max(1, Math.ceil(paragraph.length / Math.max(widthChars, 1)));
  }, 0);
  return Math.min(15 * lines + 4, 160);
}

function sheetColumnWidths(
  model: TableModel,
  preface: readonly TablePrefaceField[],
  valueWidth: number,
): { width: number }[] {
  const widths = model.columns.map((column) => columnWidth(column, model.rows));
  if (!preface.length) return widths.map((width) => ({ width }));

  const labelWidth = Math.min(
    36,
    Math.max(12, ...preface.map((field) => field.label.length + 2)),
  );
  widths[0] = Math.max(widths[0] ?? 12, labelWidth);
  if (widths.length < 2) widths.push(valueWidth);
  else widths[1] = Math.max(widths[1], valueWidth);
  return widths.map((width) => ({ width }));
}

/** Largura aproximada em caracteres, limitada para não estourar a planilha. */
function columnWidth(column: TableColumn, rows: readonly TableRow[]): number {
  const longest = rows.reduce(
    (max, row) => Math.max(max, formatTableCell(column, row).length),
    column.label.length,
  );
  return Math.min(Math.max(longest + 2, 10), 48);
}

function withExtension(fileName: string, extension: string): string {
  const base = fileName || 'dados';
  return base.toLowerCase().endsWith(`.${extension}`) ? base : `${base}.${extension}`;
}
